import React, {useCallback, useRef} from "react";
import {useMessages} from "i18n";
import {readableForegroundFor} from "./brandColors";
import {
	PALETTE_NO_COLOR,
	isPaletteNavigationKey,
	paletteNavigateFrom,
	paletteSelectedStop,
	paletteTabStop,
	type BrandPaletteEntry,
	type PaletteStop,
} from "./brandPalette";
import {
	ClearSwatchCircle, ClearSwatchSelectedRing, Swatch, SwatchCircle, SwatchRow, SwatchSelectedRing,
} from "./BrandColorPalette.styles";

/**
 * The brand-accent swatch — a wrapped row of colour circles with a ring on the chosen one.
 *
 * ONE control serves all three surfaces that pick an accent (ui#1923): the account brand sheet, the
 * sponsor colour field and the personalization builder. They differ only in the list they pass, which
 * is why {@link BrandColorPaletteProps.colors} is a required prop rather than a default — a second
 * implementation here would be a second keyboard model, a second set of colour names to translate and
 * a second opinion about what a hex means.
 *
 * It replaces the animated radial disc ui#622 introduced and ui#642 tuned (owner, 2026-09-03: "palette
 * disc can be a simple swatch"). The motion, the pointer repulsion and the reduced-motion branch that
 * existed only to switch that motion off went with it; the picker's contract did not move at all.
 *
 * This is still the ONLY accent control: the free-form native colour input is gone by owner decision.
 * Three consequences shape everything here.
 *
 * 1. A saved colour that is NOT in the offered list is a REAL, permanent state — every account branded
 *    before ui#622 shipped, and in the builder any of the 31 palette colours it does not offer. It is
 *    rendered truthfully: no circle is `aria-checked`, and nothing writes until the sender picks (with a
 *    clear stop, the first arrow press walks to a colour rather than clearing, see
 *    `paletteNavigateFrom`). The colour itself is still visible in the preview each surface shows beside
 *    this control; the account brand sheet also prints it as hex, the poster's colour rows do not.
 *    Snapping such an account to the nearest swatch would restyle their recipient pages behind their
 *    back.
 * 2. Selection is DERIVED from `value` (`paletteSelectionOf`), never mirrored into local state, so
 *    this component cannot disagree with the preview beside it.
 * 3. It is a `radiogroup`, not a grid of buttons: one tab stop, arrows to move, and each circle
 *    carries a translated colour name, because a swatch's colour is the one thing a screen-reader user
 *    cannot perceive.
 *
 * A surface may add one more stop: {@link BrandColorPaletteProps.clear}, the "no colour" swatch the
 * personalization builder offers (ui#2416). It is OPT-IN so the account and sponsor surfaces, where an
 * accent is not optional, render exactly the row they rendered before.
 *
 * Presentational only, per epic #26 — selection, the tab stop and the keyboard rules all live in the
 * React-free {@link ./brandPalette}.
 */

export type BrandColorPaletteProps = {
	/** The accent currently in the editor — the saved one until the sender picks (`useAccountBrand`). */
	value: string;
	/**
	 * The colours to offer, in the order they are shown. All 36 on the account and sponsor surfaces;
	 * the five `PALETTE_PRESETS` in the personalization builder (owner, 2026-09-03: "5 is fine").
	 */
	colors: readonly BrandPaletteEntry[];
	/** Mirrors the old swatch's `disabled`: true while a `BrandService` request is in flight. */
	disabled: boolean;
	/** Id of the visible field label, so the group is named by the same element the swatch was. */
	labelledBy: string;
	/**
	 * The "no colour" swatch, first in the row, or absent on a surface where an accent is required.
	 *
	 * The LABEL comes from the caller because the caller owns the copy: what "no colour" means differs
	 * per surface (a transfer that sets no accent of its own is not an advertiser leaving one unset).
	 */
	clear?: {label: string; onClear: () => void};
	onPick: (color: string) => void;
};

export default function BrandColorPalette({
	value, colors, disabled, labelledBy, clear, onPick,
}: BrandColorPaletteProps) {
	const m = useMessages();
	const clearable = clear !== undefined;
	const selected = paletteSelectedStop(colors, value, clearable);
	const tabStop = paletteTabStop(colors, selected, clearable);
	const swatches = useRef(new Map<PaletteStop, HTMLButtonElement | null>());

	const select = useCallback((stop: PaletteStop) => {
		if (stop === PALETTE_NO_COLOR) {
			clear?.onClear();
			return;
		}
		const entry = colors.find(candidate => candidate.id === stop);
		if (entry !== undefined) {
			onPick(entry.hex);
		}
	}, [clear, colors, onPick]);

	/*
	 * Selection follows focus, the standard `radiogroup` behaviour — and the right one here, because
	 * moving the focus ring is exactly how a keyboard user "tries on" a colour in the live preview.
	 * Landing on the clear stop clears, for the same reason: it is a choice like any other in the row.
	 * Focus is moved imperatively because the roving `tabIndex` alone does not carry it.
	 */
	const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (!isPaletteNavigationKey(event.key)) {
			return;
		}
		event.preventDefault();
		const next = paletteNavigateFrom(colors, selected, event.key, clearable);
		if (next === null) {
			return;
		}
		select(next);
		swatches.current.get(next)?.focus();
	}, [clearable, colors, select, selected]);

	return (
		<SwatchRow
			role="radiogroup"
			aria-labelledby={labelledBy}
			aria-disabled={disabled || undefined}
		>
			{clear !== undefined && (
				<Swatch
					ref={(node) => {
						swatches.current.set(PALETTE_NO_COLOR, node);
					}}
					type="button"
					role="radio"
					aria-checked={selected === PALETTE_NO_COLOR}
					aria-label={clear.label}
					tabIndex={tabStop === PALETTE_NO_COLOR ? 0 : -1}
					disabled={disabled}
					onClick={() => select(PALETTE_NO_COLOR)}
					onKeyDown={onKeyDown}
				>
					<ClearSwatchCircle>
						{selected === PALETTE_NO_COLOR && <ClearSwatchSelectedRing />}
					</ClearSwatchCircle>
				</Swatch>
			)}
			{colors.map(entry => (
				<Swatch
					key={entry.id}
					ref={(node) => {
						swatches.current.set(entry.id, node);
					}}
					type="button"
					role="radio"
					aria-checked={selected === entry.id}
					aria-label={m.account.brand.palette.colors[entry.id]}
					tabIndex={tabStop === entry.id ? 0 : -1}
					disabled={disabled}
					onClick={() => select(entry.id)}
					onKeyDown={onKeyDown}
				>
					<SwatchCircle style={{"--swatch": entry.hex} as React.CSSProperties}>
						{selected === entry.id && (
							<SwatchSelectedRing
								style={{"--ring-ink": readableForegroundFor(entry.hex)} as React.CSSProperties}
							/>
						)}
					</SwatchCircle>
				</Swatch>
			))}
		</SwatchRow>
	);
}
